import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import api, { API } from '../lib/api';
import { getMediaUrl, optimizeCloudinaryVideo } from '../lib/mediaUrl';
import { Scissors, Sparkles, Phone, CalendarDays, Printer, Download, MessageCircle, CheckCircle } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { groupServicesByCategory } from '../lib/categories';
import { SOCIAL_LINKS } from '../lib/websiteConstants';

// Sezioni caricate solo quando servono (split del codice)
const PageSpinner = () => <div className="min-h-screen flex items-center justify-center bg-[#FDF8F5]"><div className="w-9 h-9 border-4 border-[#C8617A] border-t-transparent rounded-full motion-safe:animate-spin" /></div>;
const withSuspense = (Lazy, Fallback = () => null) => (props) => <Suspense fallback={<Fallback />}><Lazy {...props} /></Suspense>;
// Extracted components
const BookingForm = withSuspense(lazy(() => import('../components/website/BookingForm')), PageSpinner);
const BookingSuccess = withSuspense(lazy(() => import('../components/website/BookingSuccess')), PageSpinner);
const MyAppointmentsModal = withSuspense(lazy(() => import('../components/website/MyAppointmentsModal')));
const TrendGallery = withSuspense(lazy(() => import('../components/website/TrendGallery')));
import BackToPreviousSection from '../components/website/BackToPreviousSection';
import {
  AnimatedSection,
  ServicesSection, SalonSection, AboutSection, PromotionsSection,
  ReviewsSection, ContactSection,
  TeamSection, WelcomeBanner, GiftCardSection, HeritageBand, GoldRule,
  DISPLAY_FONT, BRAND, CTA_PRIMARY, CTA_PRIMARY_CORE, CTA_OUTLINE_DARK_CORE, CTA_OUTLINE_LIGHT, CTA_OUTLINE_LIGHT_CORE,
} from '../components/website/sections/LandingSections';
import { setCategories } from '../lib/categories';

// Numero WhatsApp di riserva già usato in tutta la pagina
const FALLBACK_WHATSAPP = '393397833526';
const WA_TEXT = 'Ciao, vorrei prenotare un appuntamento!';

// Saluto della hero: a ogni visita una frase diversa, secondo il momento della giornata
const GREETING_INVITES = {
  morning: [
    'Ai tuoi capelli pensiamo noi, tu goditi il caffè.',
    'La giornata parte meglio con i capelli in ordine!',
    'Caffè in mano e capelli in buone mani.',
    'Oggi è la giornata giusta per piacerti ancora di più.',
  ],
  afternoon: [
    'Una pausa tutta per te? Ai capelli pensiamo noi.',
    'Regalati un pomeriggio di coccole.',
    'Un taglio, una piega e il pomeriggio cambia faccia!',
    'I tuoi capelli ti stanno facendo l\'occhiolino.',
  ],
  evening: [
    'Domani capelli nuovi? Prenoti in un minuto, anche adesso.',
    'La serata è tua, al tuo prossimo look pensiamo noi.',
    'Prima di dormire, prenota un sorriso allo specchio.',
  ],
  closed: [
    'Oggi le forbici riposano, ma la prenotazione online è sempre aperta.',
    'Forbici in vacanza, oggi! Prenota online e ci vediamo presto.',
  ],
  christmas: [
    'Capelli da festa? Noi siamo pronti!',
    'A Natale fai un regalo anche ai tuoi capelli.',
  ],
};

const pickGreetingInvite = (now, closedToday, seed) => {
  const hour = now.getHours();
  const moment = closedToday ? 'closed' : hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const isChristmas = now.getMonth() === 11 || (now.getMonth() === 0 && now.getDate() <= 6);
  const pool = [...GREETING_INVITES[moment], ...(isChristmas && !closedToday ? GREETING_INVITES.christmas : [])];
  return pool[Math.floor(seed * pool.length)];
};

// Helper per le animazioni on-scroll eleganti
function LuxSection({ children, className = "", ...props }) {
  const reduce = useReducedMotion();
  return (
    <motion.section
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={className}
      {...props}
    >
      {children}
    </motion.section>
  );
}

export default function WebsitePage() {
  const [siteData, setSiteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSlow, setLoadingSlow] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [, setShowServices] = useState(true);
  const [bookingServices, setBookingServices] = useState([]);
  const [operators, setOperators] = useState([]);
  const servicesRef = useRef(null);
  const contactRef = useRef(null);

  const [publicPromos, setPublicPromos] = useState([]);
  const [cardTemplates, setCardTemplates] = useState([]);

  // Success state
  const [success, setSuccess] = useState(false);
  const [appointmentId, setAppointmentId] = useState(null);
  const [upsellingSuggestions, setUpsellingSuggestions] = useState([]);
  const [bookingWaSent, setBookingWaSent] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  // Booking form data
  const [formData, setFormData] = useState({
    client_name: '', client_phone: '', service_ids: [], operator_id: '',
    date: format(new Date(), 'yyyy-MM-dd'), time: '09:00', notes: ''
  });
  const [blockedSlots, setBlockedSlots] = useState([]);

  // My Appointments
  const [showMyAppts, setShowMyAppts] = useState(false);
  const [bookingInitialStep, setBookingInitialStep] = useState(1);

  const [navScrolled, setNavScrolled] = useState(false);
  const [heroVisible, setHeroVisible] = useState(true);

  useEffect(() => {
    const onScroll = () => {
      setNavScrolled(window.scrollY > 80);
      setHeroVisible(window.scrollY < 500);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const slowTimer = setTimeout(() => setLoadingSlow(true), 4000);
    const fetchAll = async () => {
      // Retry fino a 3 volte: se il server è in cold start il primo tentativo può scadere
      // (Axios timeout 90s), ma il server è già sveglio al secondo tentativo (1-2s)
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          if (attempt > 0) await new Promise(r => setTimeout(r, 5000));
          const res = await api.get(`${API}/public/website`);
          const d = res.data;
          if (Array.isArray(d.categories)) setCategories(d.categories);
          setSiteData(d);
          setOperators(d.operators || []);
          setBookingServices(d.services || []);
          setCardTemplates(d.card_templates || []);
          setPublicPromos(d.promotions || []);
          clearTimeout(slowTimer);
          setLoading(false);
          return;
        } catch (err) {
          console.error(`Caricamento sito tentativo ${attempt + 1}/3 fallito:`, err);
        }
      }
      clearTimeout(slowTimer);
      setLoadFailed(true);
      setLoading(false);
    };
    fetchAll();
    // Keepalive: ping ogni 14 minuti per evitare il cold start di Render sul booking
    const keepalive = setInterval(() => {
      api.get(`${API}/ping`).catch(() => {});
    }, 14 * 60 * 1000);
    return () => { clearTimeout(slowTimer); clearInterval(keepalive); };
  }, []);

  // /prenota (Google, scontrino, pagine servizi) arriva qui con ?prenota=1: apre subito la prenotazione
  useEffect(() => {
    if (!siteData) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('prenota') !== '1') return;
    setShowBooking(true);
    window.history.replaceState(null, '', window.location.pathname);
  }, [siteData]);

  const trackBookingClick = () => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'click_prenota', {
        'event_category': 'engagement',
        'event_label': 'Apertura Modulo Prenotazione'
      });
    }
    setShowBooking(true);
  };

  const config = siteData?.config || {};
  const heroVideoUrl = config.hero_video ? getMediaUrl(config.hero_video) : '';
  const DEFAULT_HERO_POSTER = "https://res.cloudinary.com/dabpscxvz/video/upload/so_0,f_auto,q_auto,w_900/v1784728707/hero-video_hezmdf%20%27.jpg";
  const DEFAULT_HERO_VIDEO = "https://res.cloudinary.com/dabpscxvz/video/upload/f_auto,q_auto/v1784728707/hero-video_hezmdf%20%27.mp4";
  const reviews = siteData?.reviews || [];
  const gallery = siteData?.gallery || [];
  const salonPhotos = gallery.filter(g => g.section === 'salon');

  // SEO: title + meta tags dinamici + Dati Strutturati (JSON-LD)
  useEffect(() => {
    if (!siteData) return;
    const name = config.salon_name || 'Bruno Melito Hair';
    const desc = config.hero_description || `Prenota online il tuo appuntamento da ${name}. Taglio, colore, trattamenti professionali.`;
    const url = window.location.origin + '/sito';

    document.title = 'Bruno Melito Hair | Parrucchiere donna a Santa Maria Capua Vetere';

    const setMeta = (name, content, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setMeta('og:title', `${name} — Prenota Online`, true);
    setMeta('og:description', desc, true);
    setMeta('og:type', 'website', true);
    setMeta('og:url', url, true);
    if (config.hero_image) setMeta('og:image', config.hero_image, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', `${name} — Prenota Online`);
    setMeta('twitter:description', desc);

    // --- INIZIO DATI STRUTTURATI SCHEMA.ORG ---
    const schema = {
      "@context": "https://schema.org",
      "@type": "HairSalon",
      "name": name,
      "description": desc,
      "url": url,
      "telephone": config.whatsapp || (config.phones && config.phones[0]) || "",
      "image": config.hero_image ? (/^https?:\/\//.test(config.hero_image) ? config.hero_image : `${window.location.origin}${config.hero_image}`) : `${window.location.origin}/logo.png`,
      "priceRange": "€10-€60",
      "founder": { "@type": "Person", "name": "Bruno Melito" },
      "foundingDate": "1983",
      "sameAs": [
        "https://www.instagram.com/brunomelitohair/",
        "https://www.facebook.com/brunomelitoparrucchierimettilatestaaposto1983",
        "https://www.tiktok.com/@brunomelitohair.it",
        "https://www.youtube.com/@BrunoMelito",
        "https://x.com/BrunoMelitoHair",
        "https://maps.google.com/?cid=14990155446111754116"
      ],
      "potentialAction": {
        "@type": "ReserveAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://brunomelitohair.it/prenota",
          "actionPlatform": ["http://schema.org/DesktopWebPlatform", "http://schema.org/MobileWebPlatform"]
        },
        "result": { "@type": "Reservation", "name": "Prenota appuntamento" }
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": config.address || "Caserta",
        "addressCountry": "IT"
      },
      // Dati fissi, identici a quelli del blocco in index.html
      "geo": { "@type": "GeoCoordinates", "latitude": 41.0774076, "longitude": 14.265057 },
      "hasMap": "https://maps.google.com/?cid=14990155446111754116",
      // Orari reali del salone (gli stessi di index.html): non derivati da config.hours, che usa un formato libero
      "openingHoursSpecification": [
        { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Tuesday", "Wednesday", "Thursday"], "opens": "08:15", "closes": "12:30" },
        { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Tuesday", "Wednesday", "Thursday"], "opens": "14:00", "closes": "18:15" },
        { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Friday", "Saturday"], "opens": "08:00", "closes": "18:15" }
      ],
    };

    let schemaScript = document.querySelector('script[type="application/ld+json"]');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }
    schemaScript.text = JSON.stringify(schema);
    // --- FINE DATI STRUTTURATI ---

    return () => {
      document.title = 'Bruno Melito Hair';
      // Pulisce il JSON-LD quando si lascia la pagina
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) existingScript.remove();
    };
  }, [siteData, config.salon_name, config.hero_description, config.hero_image, config.hours, config.address, config.phones, config.whatsapp]);

  // Load CMS fonts — evita link duplicati
  useEffect(() => {
    if (!config.font_display && !config.font_body) return;
    const fonts = [config.font_display, config.font_body].filter(Boolean);
    if (fonts.length === 0) return;
    const href = `https://fonts.googleapis.com/css2?${fonts.map(f => `family=${f.replace(/ /g, '+')}:wght@400;600;700;800;900`).join('&')}&display=swap`;
    if (document.querySelector(`link[href="${href}"]`)) return; // già caricato
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.dataset.cmsFonts = 'true';
    document.head.appendChild(link);
    return () => { document.querySelector('link[data-cms-fonts="true"]')?.remove(); };
  }, [config.font_display, config.font_body]);

  // Il saluto della hero resta pochi secondi, poi lascia spazio al titolo
  const [showGreeting, setShowGreeting] = useState(true);
  const [greetingSeed] = useState(Math.random);
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => setShowGreeting(false), 7000);
    return () => clearTimeout(t);
  }, [loading]);

  const themeStyle = {
    '--theme-primary': config.primary_color || '#E8477C',
    '--theme-accent': config.accent_color || '#2EC4B6',
    '--theme-bg': config.bg_color || '#FAFBFD',
    '--theme-text': config.text_color || '#1A1A2E',
    '--theme-font-display': config.font_display || 'Cormorant Garamond, serif',
    '--theme-font-body': config.font_body || 'Nunito, sans-serif',
  };

  const T = {
    primary: config.primary_color || '#E8477C',
    accent: config.accent_color || '#2EC4B6',
    bg: config.bg_color || '#FAFBFD',
    text: config.text_color || '#1A1A2E',
    fontDisplay: config.font_display || 'Cormorant Garamond, serif',
    fontBody: config.font_body || 'Nunito, sans-serif',
  };

  const selectedServices = bookingServices.filter(s => formData.service_ids.includes(s.id));

  const escapeHtml = (str) => String(str || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

  // #2 — Prenota da ogni servizio: pre-seleziona il servizio e apre il form
  const bookService = (serviceId) => {
    setFormData(prev => ({ ...prev, service_ids: [serviceId] }));
    setBookingInitialStep(2);
    setShowBooking(true);
  };

  const bookPromo = (promo) => {
    setFormData(prev => ({
      ...prev,
      notes: `[PROMO: ${promo.name}]`,
      service_ids: promo.free_service_id ? [promo.free_service_id] : prev.service_ids,
    }));
    setBookingInitialStep(1);
    setShowBooking(true);
  };

  const scrollTo = (ref) => { ref.current?.scrollIntoView({ behavior: 'smooth' }); };
  const waHref = `https://wa.me/${config.whatsapp || FALLBACK_WHATSAPP}?text=${encodeURIComponent(WA_TEXT)}`;

  const handleBookingSuccess = (aptId, upsells, waSent = false) => {
    setAppointmentId(aptId);
    setUpsellingSuggestions(upsells);
    setBookingWaSent(waSent);
    setSuccess(true);
  };

  const resetBooking = () => {
    setSuccess(false);
    setShowBooking(false);
    setAppointmentId(null);
    setUpsellingSuggestions([]);
    setBookingInitialStep(1);
    setFormData({ client_name: '', client_phone: '', service_ids: [], operator_id: '', date: format(new Date(), 'yyyy-MM-dd'), time: '09:00', notes: '' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A0A10] flex flex-col items-center justify-center gap-5 px-6" role="status" aria-live="polite">
        <img src="/logo-128.png?v=1" decoding="async" alt="Bruno Melito Hair" width={64} height={64} className="w-16 h-16 rounded-2xl border-2 border-[#D4AF7A]/40" />
        <div className="w-10 h-10 border-4 border-[#D4AF7A] border-t-transparent rounded-full motion-safe:animate-spin" aria-hidden="true" />
        {loadingSlow && (
          <div className="text-center max-w-xs">
            <p className="text-[#D4AF7A] font-bold text-sm">Un momento…</p>
            <p className="text-white/75 text-sm mt-1">Il server si sta avviando.<br />Ci vorranno ancora pochi secondi.</p>
          </div>
        )}
      </div>
    );
  }

  // Tutti i tentativi falliti: niente pagina vuota, almeno i contatti diretti
  if (loadFailed && !siteData) {
    return (
      <div className="min-h-screen bg-[#FDF8F5] flex flex-col items-center justify-center gap-5 px-6 text-center" data-testid="website-load-error">
        <img src="/logo-128.png?v=1" decoding="async" alt="Bruno Melito Hair" width={64} height={64} className="w-16 h-16 rounded-2xl" />
        <h1 className="text-3xl font-semibold text-[#1A0A10]" style={{ fontFamily: DISPLAY_FONT }}>Bruno Melito Hair</h1>
        <p className="text-[#6B4A3C] max-w-sm">Non riusciamo a caricare il sito in questo momento. Puoi prenotare scrivendoci o chiamandoci.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <a href={`https://wa.me/${FALLBACK_WHATSAPP}?text=${encodeURIComponent(WA_TEXT)}`} target="_blank" rel="noopener noreferrer" className={CTA_PRIMARY}>
            <MessageCircle className="w-5 h-5" aria-hidden="true" /> WhatsApp
          </a>
          <a href={`tel:+${FALLBACK_WHATSAPP}`} className={CTA_OUTLINE_LIGHT}>
            <Phone className="w-5 h-5" aria-hidden="true" /> Chiama
          </a>
        </div>
        <button onClick={() => window.location.reload()} className="min-h-[44px] px-4 text-sm font-semibold text-[#A94C63] underline underline-offset-4">Riprova a caricare</button>
      </div>
    );
  }

  // SUCCESS PAGE
  if (success) {
    return (
      <BookingSuccess
        config={config}
        formData={formData}
        selectedServices={selectedServices}
        appointmentId={appointmentId}
        upsellingSuggestions={upsellingSuggestions}
        setUpsellingSuggestions={setUpsellingSuggestions}
        onReset={resetBooking}
        waSent={bookingWaSent}
      />
    );
  }

  // BOOKING FORM
  if (showBooking) {
    return (
      <>
        <BookingForm
          config={config}
          bookingServices={bookingServices}
          operators={operators}
          cardTemplates={cardTemplates}
          publicPromos={publicPromos}
          blockedSlots={blockedSlots}
          setBlockedSlots={setBlockedSlots}
          formData={formData}
          setFormData={setFormData}
          onBack={() => { setBookingInitialStep(1); setShowBooking(false); }}
          onSuccess={handleBookingSuccess}
          T={T}
          initialStep={bookingInitialStep}
        />
      </>
    );
  }

  // ==================== WEBSITE LANDING PAGE ====================
  const phones = config.phones || [];
  const landingServiceGroups = groupServicesByCategory(bookingServices);

  // Dynamic section ordering from CMS config
  // Ordine predefinito: prima servizi/prezzi e offerte, poi recensioni, team, salone, storia, contatti.
  // Se il CMS fornisce section_order, quello ha la precedenza (le sezioni mancanti vengono accodate).
  const defaultSectionOrder = ['services', 'promotions', 'gift_card', 'reviews', 'team', 'salon', 'about', 'trend_gallery', 'contact'];
  const rawSectionOrder = config.section_order || defaultSectionOrder;
  const normalizedSectionOrder = [...new Set(rawSectionOrder.filter(id => defaultSectionOrder.includes(id)))];
  const sectionOrder = [...normalizedSectionOrder, ...defaultSectionOrder.filter(id => !normalizedSectionOrder.includes(id))];
  const hiddenSections = config.hidden_sections || [];

  // Gruppo "Offerte": promo + Servizi Express + Gift card nella stessa sezione quando c'è qualcosa da mostrare
  const smartServices = bookingServices.filter(s => s.is_smart_offer);
  const offersGroupVisible = !hiddenSections.includes('promotions') && (publicPromos.length > 0 || smartServices.length > 0);
  const giftCardVisible = !hiddenSections.includes('gift_card');

  const renderSection = (sectionId) => {
    if (hiddenSections.includes(sectionId)) return null;
    switch (sectionId) {
      case 'services':
        return bookingServices.length > 0 ? <ServicesSection key="services" {...{ servicesRef, landingServiceGroups, setShowBooking, bookService, config, gallery }} /> : null;
      case 'salon':
        return salonPhotos.length > 0 ? <SalonSection key="salon" salonPhotos={salonPhotos} T={T} /> : null;
      case 'about':
        return config.about_title ? <AboutSection key="about" config={config} salonPhotos={salonPhotos} T={T} /> : null;
      case 'promotions':
        return offersGroupVisible ? (
          <PromotionsSection key="promotions" publicPromos={publicPromos} smartServices={smartServices}
            setShowBooking={setShowBooking} bookPromo={bookPromo} bookService={bookService}
            giftCard={giftCardVisible ? <GiftCardSection embedded whatsapp={config.whatsapp || FALLBACK_WHATSAPP} /> : null} />
        ) : null;
      case 'reviews':
        return reviews.length > 0 ? <ReviewsSection key="reviews" reviews={reviews} T={T} config={config} /> : null;
      case 'team':
        return operators.filter(o => o.active !== false).length > 0 ? <TeamSection key="team" operators={operators} setShowBooking={setShowBooking} /> : null;
      case 'trend_gallery':
        return <TrendGallery key="trend_gallery" setShowBooking={setShowBooking} buttonBg={BRAND.rose} />;
      case 'gift_card':
        // Già mostrata dentro il gruppo Offerte
        return offersGroupVisible ? null : <GiftCardSection key="gift_card" whatsapp={config.whatsapp || FALLBACK_WHATSAPP} />;
      case 'contact':
        return <ContactSection key="contact" {...{ contactRef, config, phones, setShowBooking }} />;
      default:
        return null;
    }
  };

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Buongiorno' : hour < 18 ? 'Buon pomeriggio' : 'Buonasera';
  // Nei giorni di chiusura (dagli orari del sito) l'invito ricorda la prenotazione online
  const todayHours = String((config.hours || {})[['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'][now.getDay()]] || '');
  const closedToday = /chiuso/i.test(todayHours);
  const greetingInvite = pickGreetingInvite(now, closedToday, greetingSeed);

  const goToServices = () => { setShowServices(true); setTimeout(() => scrollTo(servicesRef), 100); };
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(window.location.origin + '/sito')}&margin=8`;

  // ─── COME FUNZIONA — 3 tocchi per prenotare (dopo le recensioni) ───
  const howItWorks = (
    <LuxSection key="how_it_works" className="py-20 sm:py-24 relative overflow-hidden" style={{ background: BRAND.espresso }}>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF7A]/50 to-transparent" aria-hidden="true" />
      <div className="max-w-5xl mx-auto px-4">
        <AnimatedSection>
          <div className="text-center mb-14">
            <h2 className="text-4xl sm:text-5xl font-semibold leading-tight text-white" style={{ fontFamily: DISPLAY_FONT }}>
              Prenota in <span className="italic" style={{ color: BRAND.gold }}>3 Tocchi</span>
            </h2>
            <GoldRule />
            <p className="text-base mt-6 max-w-sm mx-auto text-white/80">
              Nessuna telefonata, nessuna attesa. Solo tu e il tuo appuntamento.
            </p>
          </div>
        </AnimatedSection>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6">
          {[
            { num: '1', Icon: Scissors, title: 'Scegli il Servizio', desc: 'Sfoglia il listino e clicca quello che vuoi' },
            { num: '2', Icon: CalendarDays, title: 'Giorno e Ora', desc: 'Slot disponibili in tempo reale, scegli il momento' },
            { num: '3', Icon: CheckCircle, title: 'Confermato Subito', desc: 'Conferma automatica immediata. Nessuna attesa' },
          ].map((step, i) => (
            <AnimatedSection key={step.num} delay={i * 0.07}>
              <div className="relative text-center px-6 pt-10 pb-8 rounded-3xl bg-white/[0.04] border border-[#D4AF7A]/25">
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full flex items-center justify-center bg-[#D4AF7A] text-[#1A0A10] font-bold text-sm" aria-hidden="true">
                  {step.num}
                </span>
                <step.Icon className="w-7 h-7 mx-auto mb-4 text-[#D4AF7A]" aria-hidden="true" />
                <h3 className="font-semibold text-xl mb-2 text-white" style={{ fontFamily: DISPLAY_FONT }}>{step.title}</h3>
                <p className="text-sm leading-relaxed text-white/80">{step.desc}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
        <AnimatedSection delay={0.3}>
          <div className="text-center mt-12">
            <button onClick={trackBookingClick} className={`${CTA_PRIMARY_CORE} min-h-[56px] px-10 text-base`}>
              <Scissors className="w-5 h-5" aria-hidden="true" />
              Prenota Ora — Conferma Immediata
            </button>
            <p className="text-sm mt-3 text-white/75">
              Nessuna registrazione · Gratuito · Sempre disponibile
            </p>
          </div>
        </AnimatedSection>
      </div>
    </LuxSection>
  );

  return (
    <div className="min-h-screen text-white website-landing" style={{ ...themeStyle, backgroundColor: BRAND.cream, fontFamily: `var(--theme-font-body)` }} data-testid="website-landing">
      <style>{`
        .website-landing ::selection { background: #D4AF7A; color: #1A0A10; }
        .hero-animate { opacity: 1; }
        @media (prefers-reduced-motion: no-preference) {
          @keyframes heroFadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
          .hero-animate { animation: heroFadeIn 0.9s cubic-bezier(0.22, 1, 0.36, 1) both; }
          .hero-d1 { animation-delay: 0.1s; }
          .hero-d2 { animation-delay: 0.2s; }
          .hero-d3 { animation-delay: 0.3s; }
          .hero-d4 { animation-delay: 0.4s; }
          .hero-d5 { animation-delay: 0.5s; }
          .hero-greeting { transition: grid-template-rows 0.8s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.6s ease; }
          @keyframes greetingPop { 0% { opacity: 0; transform: translateY(16px) scale(0.85); } 60% { opacity: 1; transform: translateY(0) scale(1.04); } 100% { transform: scale(1); } }
          .greeting-pop { animation: greetingPop 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both; }
          @keyframes greetingSnip { 0%, 100% { transform: rotate(0deg); } 25% { transform: rotate(-16deg); } 75% { transform: rotate(14deg); } }
          .greeting-snip { animation: greetingSnip 0.5s ease-in-out 0.9s 3; }
          @keyframes greetingTwinkle { 0%, 100% { opacity: 0.45; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1.15); } }
          .greeting-twinkle { animation: greetingTwinkle 1.6s ease-in-out infinite; }
          .greeting-twinkle-late { animation-delay: 0.8s; }
        }
      `}</style>

      {/* NAVBAR — una sola riga su mobile + striscia Servizi/Contatti dentro la nav (niente sovrapposizioni) */}
      <nav aria-label="Navigazione principale" className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow] duration-300 border-b border-[#1A0A10]/10 ${navScrolled ? 'bg-[#FDF8F5] shadow-[0_4px_16px_rgba(26,10,16,0.08)]' : 'bg-[#FDF8F5]/85 backdrop-blur-sm'}`}>
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-2">
          <a href="/sito" className="flex items-center gap-2.5 min-w-0 min-h-[44px] text-[#1A0A10]">
            <img src="/logo-128.png?v=1" decoding="async" alt={config.salon_name || 'Bruno Melito Hair'} width={36} height={36} className="w-9 h-9 rounded-lg shrink-0" />
            <span className="hidden min-[400px]:block min-w-0 truncate font-semibold text-base sm:text-lg" style={{ fontFamily: DISPLAY_FONT }}>
              <span className="sm:hidden">Bruno Melito</span>
              <span className="hidden sm:inline">{config.salon_name || 'Bruno Melito Hair'}</span>
            </span>
          </a>
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="hidden md:flex items-center gap-1 text-sm">
              <button onClick={goToServices} className="min-h-[44px] px-3 rounded-full font-semibold text-[#1A0A10]/80 hover:text-[#A94C63] transition-colors">Servizi</button>
              <button onClick={() => scrollTo(contactRef)} className="min-h-[44px] px-3 rounded-full font-semibold text-[#1A0A10]/80 hover:text-[#A94C63] transition-colors">Contatti</button>
              <div className="hidden xl:flex items-center border-l border-[#1A0A10]/15 pl-2 ml-1">
                {SOCIAL_LINKS.map((link, i) => (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" aria-label={link.label} title={link.label}
                    className="w-11 h-11 inline-flex items-center justify-center rounded-full text-[#1A0A10]/65 hover:text-[#1A0A10] hover:bg-[#1A0A10]/5 transition-colors">
                    <link.icon className="w-4 h-4" aria-hidden="true" />
                  </a>
                ))}
              </div>
            </div>
            <a href="/login" data-testid="admin-link" aria-label="Accedi all'area riservata" title="Area Riservata"
              className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center gap-1.5 px-2 lg:px-3 rounded-full text-[#1A0A10]/70 hover:text-[#1A0A10] hover:bg-[#1A0A10]/5 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
              <span className="hidden lg:inline text-sm font-semibold">Accedi</span>
            </a>
            <button onClick={() => setShowMyAppts(true)} data-testid="my-appointments-btn"
              aria-label="I miei appuntamenti: verifica la tua prenotazione" title="Inserisci il tuo numero di telefono per vedere le tue prenotazioni"
              className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center gap-1.5 px-2 lg:px-3 rounded-full text-[#1A0A10]/70 hover:text-[#1A0A10] hover:bg-[#1A0A10]/5 transition-colors">
              <CalendarDays className="w-5 h-5" aria-hidden="true" />
              <span className="hidden lg:inline text-sm font-semibold whitespace-nowrap">I Miei Appuntamenti</span>
            </button>
            <button onClick={trackBookingClick} data-testid="website-book-btn" className={`${CTA_PRIMARY_CORE} min-h-[44px] px-4 sm:px-6 text-sm whitespace-nowrap`}>
              <span className="sm:hidden">Prenota</span>
              <span className="hidden sm:inline">Prenota ora</span>
            </button>
          </div>
        </div>
        {/* Striscia mobile/tablet: parte della nav, quindi sempre sotto la prima riga */}
        <div className="md:hidden flex items-center justify-center border-t border-[#1A0A10]/10">
          <button onClick={goToServices} className="min-h-[44px] px-6 text-sm font-semibold text-[#1A0A10]/85 hover:text-[#A94C63] transition-colors">Servizi</button>
          <span className="w-px h-4 bg-[#1A0A10]/20" aria-hidden="true" />
          <button onClick={() => scrollTo(contactRef)} className="min-h-[44px] px-6 text-sm font-semibold text-[#1A0A10]/85 hover:text-[#A94C63] transition-colors">Contatti</button>
        </div>
      </nav>

      {/* HERO - CINEMATIC VIDEO EXPERIENCE */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
        {/* Sfondo Video */}
        <video
          key={heroVideoUrl}
          autoPlay
          loop
          muted
          playsInline
          poster={heroVideoUrl ? (optimizeCloudinaryVideo(heroVideoUrl, true) || undefined) : DEFAULT_HERO_POSTER}
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden="true"
        >
          <source src={heroVideoUrl ? optimizeCloudinaryVideo(heroVideoUrl) : DEFAULT_HERO_VIDEO} type="video/mp4" />
        </video>

        {/* Overlay scuro elegante per leggibilità */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A0A10]/80 via-[#1A0A10]/65 to-[#1A0A10]/90"></div>

        {/* Contenuto Testo Centrato */}
        <div className="relative z-10 text-center text-white px-6 pt-36 pb-20 md:pt-28 max-w-4xl mx-auto">

          {/* Saluto: chiaro e allegro sopra il video, con una frase diversa a ogni visita; sparisce dopo 7 secondi */}
          <div className={`hero-greeting grid ${showGreeting ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} aria-hidden={!showGreeting}>
          <div className="overflow-hidden">
          <div className="greeting-pop relative mb-6 mt-7 mx-1 inline-flex flex-col items-center gap-1.5 rounded-3xl bg-[#FDF8F5] px-7 pt-8 pb-4 sm:px-10 shadow-[0_12px_40px_rgba(200,97,122,0.45)]" data-testid="hero-greeting">
            <span className="absolute -top-5 left-1/2 -ml-5 w-10 h-10 rounded-full bg-[#C8617A] flex items-center justify-center shadow-md" aria-hidden="true">
              <Scissors className="greeting-snip w-5 h-5 text-white" />
            </span>
            <Sparkles className="greeting-twinkle absolute top-3 left-4 w-5 h-5 text-[#D4AF7A]" aria-hidden="true" />
            <Sparkles className="greeting-twinkle greeting-twinkle-late absolute bottom-3 right-4 w-4 h-4 text-[#D4AF7A]" aria-hidden="true" />
            <p className="text-3xl sm:text-4xl italic text-[#A94C63] leading-none" style={{ fontFamily: DISPLAY_FONT }}>
              {greeting}!
            </p>
            <p className="text-base sm:text-lg font-medium text-[#2D1B14] leading-snug max-w-sm">{greetingInvite}</p>
            <p className="mt-1 text-[11px] sm:text-xs font-semibold tracking-[0.3em] uppercase text-[#8A6D3B]">
              {config.year_founded ? `Hair Stylist dal ${config.year_founded}` : (config.subtitle || 'Solo per Appuntamento')}
            </p>
          </div>
          </div>
          </div>

          <h1 className="font-black mb-6" style={{ fontFamily: DISPLAY_FONT, lineHeight: 0.9 }}>
            <span className="block hero-animate hero-d2" style={{ fontSize: 'clamp(3.5rem, 12vw, 6rem)', letterSpacing: '-0.03em' }}>Bruno</span>
            <span className="block hero-animate hero-d3 italic font-normal text-[#C8617A]" style={{ fontSize: 'clamp(3.5rem, 12vw, 6rem)', letterSpacing: '-0.02em' }}>Melito</span>
          </h1>

          <p className="text-base md:text-lg text-white/85 max-w-xl mx-auto mb-10 leading-relaxed hero-animate hero-d4">
            {config.hero_description || "L'eccellenza dell'hair styling a Santa Maria Capua Vetere. Trattamenti premium, colore senza ammoniaca e stile personalizzato."}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center hero-animate hero-d5">
            <button onClick={trackBookingClick} className={`${CTA_PRIMARY_CORE} min-h-[56px] px-10 text-sm`} data-testid="website-hero-book-btn">
              Prenota Ora
            </button>
            <button onClick={goToServices} className={`${CTA_OUTLINE_DARK_CORE} min-h-[56px] px-8 text-sm`}>
              Scopri i Servizi
            </button>
            {phones[0] && (
              <a href={`tel:${String(phones[0]).replace(/\s/g, '')}`} className={`${CTA_OUTLINE_DARK_CORE} min-h-[56px] px-8 text-sm`}>
                <Phone className="w-4 h-4" aria-hidden="true" /> Chiama
              </a>
            )}
          </div>
        </div>

        {/* Freccia Scroll */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 hero-animate hero-d5 z-10" aria-hidden="true">
          <span className="text-white/75 text-xs font-semibold tracking-[0.3em] uppercase">Scorri</span>
          <div className="w-px h-12 bg-gradient-to-b from-white/60 to-transparent"></div>
        </div>
      </section>

      {/* "Prima volta da noi?" — striscia in linea, chiudibile */}
      <WelcomeBanner setShowBooking={setShowBooking} />

      {/* "Dal {anno}": numeri ricavati solo dai dati del salone */}
      <HeritageBand config={config} servicesCount={bookingServices.length} reviews={reviews} />

      {/* Sezioni dinamiche (ordine dal CMS); "Come funziona" subito dopo le recensioni */}
      {(() => {
        const out = [];
        sectionOrder.forEach((id) => {
          const sec = renderSection(id);
          if (sec) out.push(sec);
          if (id === 'reviews') out.push(howItWorks);
        });
        return out;
      })()}

      {/* QR CODE SECTION — solo da tablet/desktop (su telefono non serve) */}
      <LuxSection className="hidden sm:block py-16 sm:py-20" style={{ background: BRAND.cream }} data-testid="qr-code-section">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#1A0A10]" style={{ fontFamily: DISPLAY_FONT }}>Inquadra e Prenota</h2>
            <p className="text-[#6B4A3C] mt-3 max-w-md mx-auto">Scansiona il QR Code con il tuo smartphone per prenotare direttamente il tuo prossimo appuntamento</p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
            <div className="bg-white rounded-3xl p-8 shadow-[0_8px_24px_rgba(26,10,16,0.06)] border border-[#1A0A10]/10 flex flex-col items-center" id="qr-print-area" data-testid="qr-code-card">
              <div className="bg-white p-3 rounded-2xl border border-[#D4AF7A]/50">
                <img
                  src={qrSrc}
                  alt="QR Code Prenotazione"
                  width={200}
                  height={200}
                  className="block"
                  data-testid="qr-code-img"
                />
              </div>
              <p className="font-semibold text-lg mt-4 text-[#1A0A10]" style={{ fontFamily: DISPLAY_FONT }}>{config.salon_name || 'BRUNO MELITO HAIR'}</p>
              <p className="text-sm text-[#6B4A3C] mt-1">Prenota il tuo appuntamento</p>
              {config.address && <p className="text-sm text-[#6B4A3C] mt-1">{config.address}</p>}
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  const printContent = document.getElementById('qr-print-area');
                  const imgSrc = printContent.querySelector('img')?.src || '';
                  const win = window.open('', '_blank');
                  win.document.write(`
                    <html><head><title>QR Code - ${escapeHtml(config.salon_name || 'Bruno Melito Hair')}</title>
                    <style>
                      @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&display=swap');
                      body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #fff; }
                      .card { text-align: center; padding: 60px 40px; }
                      .card h1 { font-family: 'Playfair Display', serif; font-size: 28px; margin: 24px 0 8px; color: #1C1008; }
                      .card p { font-size: 16px; color: #64748B; margin: 4px 0; }
                      .card .addr { font-size: 13px; color: #94A3B8; }
                      .card .hint { font-size: 14px; color: #C8617A; font-weight: bold; margin-top: 16px; }
                      img { display: block; margin: 0 auto; }
                    </style></head><body>
                    <div class="card">
                      <img src="${escapeHtml(imgSrc)}" width="250" height="250" />
                      <h1>${escapeHtml(config.salon_name || 'BRUNO MELITO HAIR')}</h1>
                      <p>Prenota il tuo appuntamento</p>
                      ${config.address ? `<p class="addr">${escapeHtml(config.address)}</p>` : ''}
                      <p class="hint">Inquadra il QR Code con la fotocamera</p>
                    </div>
                    </body></html>
                  `);
                  win.document.close();
                  setTimeout(() => { win.print(); }, 500);
                }}
                className={CTA_PRIMARY}
                data-testid="qr-print-btn"
              >
                <Printer className="w-5 h-5" aria-hidden="true" /> Stampa QR Code
              </button>
              <button
                onClick={() => {
                  const imgSrc = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(window.location.origin + '/sito')}&margin=20`;
                  const link = document.createElement('a');
                  link.href = imgSrc;
                  link.download = 'qr-code-bruno-melito.png';
                  link.target = '_blank';
                  link.click();
                  toast.success('QR Code scaricato!');
                }}
                className={CTA_OUTLINE_LIGHT}
                data-testid="qr-download-btn"
              >
                <Download className="w-5 h-5" aria-hidden="true" /> Scarica Immagine
              </button>
            </div>
          </div>
        </div>
      </LuxSection>

      <section className="py-20 bg-[#F6ECE6]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-[#1A0A10]" style={{ fontFamily: DISPLAY_FONT }}>Seguici sui social</h2>
          <p className="text-base text-[#6B4A3C] mt-3">Vieni a trovarci anche online.</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mt-8">
            <a href="https://www.instagram.com/brunomelitohair/" target="_blank" rel="noopener noreferrer" className={CTA_OUTLINE_LIGHT}>Instagram</a>
            <a href="https://www.facebook.com/brunomelitoparrucchierimettilatestaaposto1983" target="_blank" rel="noopener noreferrer" className={CTA_OUTLINE_LIGHT}>Facebook</a>
            <a href="https://www.tiktok.com/@brunomelitohair.it" target="_blank" rel="noopener noreferrer" className={CTA_OUTLINE_LIGHT}>TikTok</a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="pt-12 pb-28 sm:pb-12 relative text-white" style={{ backgroundColor: BRAND.espresso }}>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF7A] to-transparent" aria-hidden="true" />
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col items-center gap-6 text-center">
            <img src="/logo-128.png?v=1" decoding="async" loading="lazy" alt={config.salon_name || 'Bruno Melito Hair'} width={56} height={56} className="w-14 h-14 rounded-2xl border border-[#D4AF7A]/30" />
            <p className="text-white text-lg font-semibold" style={{ fontFamily: DISPLAY_FONT }}>{config.salon_name || 'Bruno Melito Hair'}</p>
            <div className="flex items-center gap-2">
              {SOCIAL_LINKS.map((link, i) => (
                <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" aria-label={link.label} title={link.label}
                  className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-white/80 hover:text-white hover:border-[#D4AF7A] transition-colors">
                  <link.icon className="w-5 h-5" aria-hidden="true" />
                </a>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-4 text-sm text-white/80">
              <a href="/sito" className="inline-flex items-center min-h-[44px] px-2 hover:text-white transition-colors">Prenota Online</a>
              <a href={waHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center min-h-[44px] px-2 hover:text-white transition-colors">WhatsApp</a>
              {config.maps_url && (
                <a href={config.maps_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center min-h-[44px] px-2 hover:text-white transition-colors">Come Raggiungerci</a>
              )}
            </div>
            {config.address && <p className="text-white/75 text-sm">{config.address}</p>}
            <p className="text-white/70 text-xs">&copy; {new Date().getFullYear()} {config.salon_name || 'Bruno Melito Hair'}. Tutti i diritti riservati.</p>
          </div>
        </div>
      </footer>

      {/* Mobile bottom bar — WhatsApp (secondaria) + Prenota (primaria) */}
      <div className="fixed bottom-0 left-0 right-0 p-3 bg-[#FDF8F5]/95 backdrop-blur-md border-t border-[#1A0A10]/10 sm:hidden z-50 shadow-[0_-4px_20px_rgba(26,10,16,0.08)] flex gap-2">
        <a
          href={waHref}
          target="_blank" rel="noopener noreferrer"
          aria-label="Scrivici su WhatsApp"
          className={`${CTA_OUTLINE_LIGHT_CORE} min-h-[52px] px-4 text-sm`}
          data-testid="website-mobile-wa-btn"
        >
          <MessageCircle className="w-5 h-5 shrink-0" aria-hidden="true" />
          <span>WhatsApp</span>
        </a>
        <button onClick={trackBookingClick} className={`${CTA_PRIMARY_CORE} flex-1 min-h-[52px] px-4 text-sm`} data-testid="website-mobile-book-btn">
          <Scissors className="w-5 h-5" aria-hidden="true" /> Prenota ora
        </button>
      </div>

      {/* WhatsApp floating button (desktop, bottom-right) */}
      <a
        href={waHref}
        target="_blank" rel="noopener noreferrer"
        aria-label="Scrivici su WhatsApp"
        className="hidden sm:flex fixed bottom-6 right-6 z-50 items-center gap-2 min-h-[48px] bg-[#1A0A10] border border-[#D4AF7A]/60 hover:border-[#D4AF7A] text-white font-semibold text-sm px-4 rounded-full shadow-[0_8px_24px_rgba(26,10,16,0.3)] transition-colors group"
        title="Scrivici su WhatsApp"
        data-testid="whatsapp-float-btn"
      >
        <MessageCircle className="w-5 h-5 text-[#D4AF7A]" aria-hidden="true" />
        <span className="max-w-0 overflow-hidden group-hover:max-w-[120px] group-focus-visible:max-w-[120px] transition-[max-width] duration-300 whitespace-nowrap">Scrivici ora</span>
      </a>

      {/* Floating PRENOTA pill — appare dopo hero, desktop */}
      {!heroVisible && (
        <div className="hidden sm:block fixed bottom-8 left-1/2 -translate-x-1/2 z-40">
          <button
            onClick={trackBookingClick}
            className={`${CTA_PRIMARY_CORE} min-h-[52px] px-7 text-sm shadow-[0_10px_25px_rgba(169,76,99,0.35)]`}
            style={{ transition: 'transform 0.2s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.2s ease' }}
            onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.96)'; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            data-testid="floating-book-btn"
          >
            <Scissors className="w-4 h-4" aria-hidden="true" />
            Prenota ora
          </button>
        </div>
      )}

      {/* MY APPOINTMENTS MODAL */}
      {showMyAppts && (
        <MyAppointmentsModal
          onClose={() => setShowMyAppts(false)}
          onRebook={({ service_ids, client_name, client_phone }) => {
            setFormData(prev => ({
              ...prev,
              service_ids,
              client_name: client_name || prev.client_name,
              client_phone: client_phone || prev.client_phone,
            }));
            trackBookingClick();
          }}
        />
      )}

      {/* BACK TO PREVIOUS SECTION BUTTON */}
      <BackToPreviousSection />
    </div>
  );
}
